import { Controller } from '@hotwired/stimulus'
import { FrameElement } from '@hotwired/turbo'
import DrawerController from './drawer'

export default class DrawerFrameController extends Controller<FrameElement> {
  declare readonly drawerOutlets?: DrawerController[]
  declare readonly hasDrawerOutlet: boolean

  static outlets = ['drawer']

  connect() {
    this.element.addEventListener('drawer:close', this.#handleDrawerClose)
  }

  disconnect() {
    this.element.removeEventListener('drawer:close', this.#handleDrawerClose)
  }

  drawerOutletConnected(outlet: DrawerController) {
    outlet.open()
    this.#stackDrawers()
  }

  drawerOutletDisconnected() {
    this.#stackDrawers()
  }

  // Private methods

  #handleDrawerClose = () => {
    if (!this.hasDrawerOutlet) return

    this.#stackDrawers(true)
  }

  #stackDrawers(afterClose: boolean = false) {
    if (!this.hasDrawerOutlet) return

    // Drawer closing animation takes time. When going between two drawers, the
    // restacking mechanism attempts to apply a new depth value to the exiting
    // drawer which inadvertently causes it to snap back into view. So, only
    // restack drawers that are not already exiting. Except on the `afterClose`
    // pass, which triggers the drawer exit in the first place.
    const drawers = afterClose ? this.drawerOutlets : this.drawerOutlets.filter(drawer => !drawer.exitingValue)
    const numberOfDrawers = drawers.length

    drawers.forEach((drawer, index) => {
      // This code determines how to set the depth of the drawers.
      // If we've reached this step after closing the top-most drawer, we want
      // to set its depth to `-1` to signify that it's exiting the view.
      // The drawer directly underneath it will have a depth of `0` and so on.
      // Otherwise, we've reached this code by opening the drawer, and we want
      // to start at a depth of `0`.
      const depthThreshold = afterClose ? 2 : 1
      const depth = numberOfDrawers - index - depthThreshold

      drawer.depthValue = depth
      drawer.indexValue = index
    })
  }
}
